<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'analyticsHome' }"
          marginBottom="5"/>

      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Davon vorzeitig beendet
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">Einzelcoaching</h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :officeDropdown="officeDropdown"
            @search="getCoachingList"/>

        <div class="row align-items-center justify-content-between">
          <div class="mt-3">
            <PerPage :values="[10, 25, 50, 100]" @update:perPage="getCoachingList"/>

            <ExcelExportButton
                style="margin-right: 15px;"
                :label="$t('general.excel_export')"
                :loading="isExcelLoaded"
                @click="getExcelExport"/>
          </div>
        </div>

        <div class="table-responsive overflow-auto pt-4 mb-3">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Standort</th>
              <th class="text-end">Gesamt</th>
              <th class="text-end">Beendet</th>
              <th class="text-end">Abgebrochen</th>
              <th class="text-end">Abbruchquote (% von Gesamt)</th>
              <th class="text-end">Abbruchquote (% von Beendet)</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="office in coachingList?.data" :key="office.officeId">
              <td>{{ office.officeName }}</td>
              <td class="text-end">{{ office.terminatedCoachingData.totalCount }}</td>
              <td class="text-end">{{ office.terminatedCoachingData.endedCount }}</td>
              <td class="text-end">{{ office.terminatedCoachingData.terminatedCount }}</td>
              <td class="text-end">{{ office.terminatedCoachingData.terminatedPctOfAll }} %</td>
              <td class="text-end">{{ office.terminatedCoachingData.terminatedPctOfEnded }} %</td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="(coachingList?.pagination?.total_pages ?? 0) > 1"
            :totalPages="coachingList?.pagination?.total_pages"
            :currentPage="coachingList?.pagination?.current_page"
            :totalItems="coachingList?.pagination?.total_items"
            :perPage="coachingList?.pagination?.items_per_page"
            @pagechanged="getCoachingList"/>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup lang="ts">
import analyticsService from '@/api/Analytics';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast} from '@/components/VueToast';
// @ts-ignore
import {getOfficeDropdown} from '@/shared/dropdowns';
import {downloadFile} from '@/shared/functions';
import Search from '@/views/App/Analytics/TerminatedCoaching/PrivateCoaching/Search.vue';
import {onMounted, ref} from 'vue';
import ExcelExportButton from "@/components/ExcelExportButton.vue";
import BackButton from "@/components/button/BackButton.vue";

// ---------- Types ----------

interface ITerminatedCoachingStats {
  totalCount: number;
  endedCount: number;
  terminatedCount: number;
  terminatedPctOfAll: number;
  terminatedPctOfEnded: number;
}

interface ITerminatedCoachingOffice {
  officeId: number;
  officeName: string;
  terminatedCoachingData: ITerminatedCoachingStats;
}

interface IPagination {
  total_pages: number;
  current_page: number;
  items_per_page?: number;
  total_items?: number;
}

interface ICoachingList {
  data: ITerminatedCoachingOffice[];
  pagination: IPagination;
}

interface IFilters {
  officeIds: number[];
  startDate: string;
  endDate: string;
}

// ---------- State ----------

const currentYear = new Date().getFullYear();

const filters = ref<IFilters>({
  officeIds: [],
  startDate: `01.01.${currentYear}`,
  endDate: `31.12.${currentYear}`,
});

const coachingList = ref<ICoachingList>();
const officeDropdown = ref([]);
const isSearching = ref(false);
const isLoaded = ref(false);
const isExcelLoaded = ref(false);

async function getExcelExport() {
  await downloadFile('/analytics/terminated/private-coaching/excel-export',
      undefined,
      {
        ...filters.value,
        'officeIds[]': filters.value.officeIds,
      },
      false,
      'GET',
      isExcelLoaded
  )
}

async function getCoachingList(
    page = 1,
    perPage?: number,
    passedFilters?: IFilters
) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = coachingList.value?.pagination?.items_per_page;

  try {
    const {data} = await analyticsService.getTerminatedPrivateCoachingList({
      ...(passedFilters ? passedFilters : filters.value),
      page: page,
      limit: perPage ?? perPageRes,
    });

    coachingList.value = data.data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

onMounted(async () => {
  await Promise.all([
    getOfficeDropdown(officeDropdown),
    getCoachingList()
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>

</style>
